import { useState, useCallback, useEffect } from 'react'
import { TFunction } from 'i18next'
import { DotsLoading } from '../Loading'
import { createPortal } from 'react-dom'
import { FaTimes } from 'react-icons/fa'
import './ConfirmDialog.css'

interface ConfirmDialogProps {
  isOpen: boolean
  message: string
  onConfirm: () => void | Promise<void>
  onClose: () => void
  translate: TFunction
  confirmText?: string
  cancelText?: string
}

export const ConfirmDialog = ({
  isOpen,
  message,
  onConfirm,
  onClose,
  translate,
  confirmText = 'message.confirm',
  cancelText = 'message.cancel',
}: ConfirmDialogProps) => {
  const [isConfirmLoading, setIsConfirmLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const handleConfirm = useCallback(async () => {
    try {
      setIsConfirmLoading(true)
      await onConfirm()
    } finally {
      setIsConfirmLoading(false)
      onClose()
    }
  }, [onConfirm, onClose])

  const handleClose = useCallback(() => {
    if (!isConfirmLoading) {
      setIsConfirmLoading(false)
      onClose()
    }
  }, [isConfirmLoading, onClose])

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 cursor-pointer backdrop-blur-md dialog-overlay"
        onClick={handleClose}
      />

      <div className="relative bg-[var(--card-bg)] rounded-lg shadow-2xl w-full max-w-md z-10 dialog-content">
        {/* Header */}
        <div className="active-gradient text-[var(--card-title)] px-6 py-3 rounded-t-lg">
          <div className="flex items-center justify-between">
            <h4 className="text-black text-xl font-semibold">
              {translate('confirmDialog.confirmAction')}
            </h4>
            <button
              type="button"
              onClick={handleClose}
              className="text-black hover:text-[var(--color-accent-800)] transition-colors p-1 rounded-full"
              aria-label="Close modal"
            >
              <FaTimes size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 bg-[var(--modal-content-bg-secondary)]">
          <p className="text-[var(--bet-type-text)] text-lg font-medium text-left">
            {message}
          </p>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[var(--card-divider)] flex justify-end gap-3">
          <button
            type="button"
            className="px-6 py-2 bg-[var(--bet-type-bg)] text-[var(--bet-type-text)] border border-[var(--bet-type-border)] hover:bg-[var(--bet-type-hover-bg)] hover:text-[var(--bet-type-hover-text)] hover:border-[var(--bet-type-hover-border)] rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            onClick={handleClose}
            disabled={isConfirmLoading}
          >
            {translate(cancelText)}
          </button>
          <button
            type="button"
            className="px-6 py-2 active-gradient text-black font-medium rounded-lg hover:opacity-90 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            onClick={() => !isConfirmLoading && handleConfirm()}
            disabled={isConfirmLoading}
          >
            {isConfirmLoading && <DotsLoading size="sm" />}
            {translate(confirmText)}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default ConfirmDialog
